import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useLocation, useParams } from "react-router-dom-v5-compat";
import {
  CreateSessionResponseEnum,
  GetSessionInfoResponseEnum,
} from "@b2bportal/auth-api";
import { getSessionInfo, impersonateUserAsAgent } from "@hopper-b2b/api";
// eslint-disable-next-line @nx/enforce-module-boundaries
import { IndeterminateLoader } from "@hopper-b2b/ui";
import { PATH_HOME } from "@hopper-b2b/utilities";

import { getSessionInfoSelector, updateSession } from "../../store";

interface AgentImpersonationProps {
  /**
   * The route to redirect to after the impersonation is successful
   * @default PATH_HOME ("/")
   */
  redirectPath?: string;
  refreshHeaderKey?: string;
  refreshStorageKey?: string;
}

type ImpersonationUrlParams = {
  tenantId?: string;
  userId?: string;
};

/**
 * @see https://hopper-jira.atlassian.net/wiki/spaces/CXPROD/pages/6695223378/Agent+Impersonation for usage guide
 * @param {AgentImpersonationProps} props
 * @return {JSX.Element}
 */
const AgentImpersonation = (props: AgentImpersonationProps): JSX.Element => {
  const {
    redirectPath = PATH_HOME,
    refreshHeaderKey,
    refreshStorageKey,
  } = props;
  const dispatch = useDispatch();
  const { tenantId = "", userId = "" } = useParams<ImpersonationUrlParams>();
  const { hash } = useLocation();
  const sessionInfo = useSelector(getSessionInfoSelector);
  const [isDone, setIsDone] = useState(false);
  const [error, setError] = useState("");

  const impersonateUser = useCallback(
    async (tenantId: string, userId: string) => {
      if (refreshStorageKey) {
        // deleteRefreshToken
        localStorage.removeItem(refreshStorageKey);
      }

      const tenantData = hash.substring(1);

      try {
        const impersonateRes = await impersonateUserAsAgent(
          userId,
          tenantId,
          tenantData,
          refreshHeaderKey,
          refreshStorageKey
        );

        if (impersonateRes !== null && impersonateRes !== undefined) {
          // RBC edge case until RBC-1997 is done
          const isRBCResponse =
            tenantId === "rbc" &&
            typeof impersonateRes === "string" &&
            impersonateRes === "";
          // For tenants that implement b2b-interfaces - we do this check
          // because it's possible to send a 200 with a CreateSessionFailed
          // response
          const isStdResponse =
            typeof impersonateRes === "object" &&
            impersonateRes.CreateSessionResponse ===
              CreateSessionResponseEnum.CreateSessionSuccess;

          if (isRBCResponse || isStdResponse) {
            const userSessionRes = await getSessionInfo();

            if (
              userSessionRes.GetSessionInfoResponse ===
              GetSessionInfoResponseEnum.Session
            ) {
              dispatch(updateSession(userSessionRes.sessionInfo));
            }
          }
        }
      } catch (error) {
        setError((error as Error).message);
      } finally {
        setIsDone(true);
      }
    },
    [dispatch, hash, refreshHeaderKey, refreshStorageKey]
  );

  useEffect(() => {
    if (tenantId && userId && !sessionInfo) {
      impersonateUser(tenantId, userId);
    }
  }, [impersonateUser, tenantId, sessionInfo, userId]);

  if (!isDone) {
    return <IndeterminateLoader />;
  }

  if (error) {
    return <p>{error}</p>;
  }

  return <Navigate to={redirectPath} />;
};

export default AgentImpersonation;
